import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

import {
  MenuMetadataForChannel,
  SelectableMenuItem,
  ItemSelectorDetails,
  ItemCardCustomization,
  BuilderMode,
  ItemCardSelectionInfoKind,
  NutritionFactName,
  TagType,
  SelectableMenuItemSource,
  SelectableVariantMenuItem,
  MealBuilderItemSection,
  ItemCardCustomizationType,
  SelectableItemCardCustomization,
  GroupedItemCustomization,
  PriceType,
  Restaurant,
  UniversalItemDetails,
  UniversalMenuItem,
  Tag,
  VariantMenuItem
} from '@/models';
import { getDefaultGroupedMenuItem, isDefined } from '@/helpers/util';
import { findMetadataForMenuItem, getRewardsExclusiveTagText, isMenuItemRewardsExclusive } from '@/helpers/metadata';
import { formatPriceRange, formatCalorieRange } from '@/services/meal-builder/utils/formatting';
import map from 'lodash/map';
import { capitalizeEveryWord } from '@/helpers/formatting';
import { ui } from '@/constants';

export interface ItemSelectedEventArgs<TArgs = any> {
  item: SelectableMenuItem;
  args: TArgs;
}

@Component({
  name: 'CmgMealBuilderItemSelector',
})
export default class CmgMealBuilderItemSelector extends Vue {
  @Prop({ type: Object })
  itemSection?: MealBuilderItemSection;

  @Prop({ required: false, type: Array })
  items!: SelectableMenuItem[];

  @Prop({ required: false, type: Array })
  universalItems!: UniversalMenuItem[];

  @Prop({ required: true, type: Object })
  metadata!: MenuMetadataForChannel;

  @Prop({ required: true, type: String })
  title!: string;

  @Prop({ type: String })
  subtitle?: string;

  @Prop({ default: false, type: Boolean })
  showSelectNone!: boolean;

  @Prop({ default: false, type: Boolean })
  selectNoneSelected!: boolean;

  @Prop({ default: PriceType.Default, type: String})
  variantPriceType!: string;

  @Prop({ type: Function })
  itemNameSelector?: (item: SelectableMenuItem) => string;

  @Prop({ type: Function })
  isItemSelected?: (item: SelectableMenuItem) => boolean;

  get builderMode(): BuilderMode | null {
    return this.$appStore.state.mealBuilder.builderMode;
  }

  get customizingItem(): SelectableMenuItem | null {
    return this.$appStore.state.mealBuilder.customizingItem;
  }

  get itemDetails(): ItemSelectorDetails[] {
    if (!this.builderMode) {
      return [];
    }
    return this.items
      .map(item => this.processItemDetails(item))
      .filter(isDefined);
  }

  get selectNoneImageUrl(): string {
    return this.$appStore.state.cms.images.icons.selectNone.default;
  }

  get selectNoneAriaLabel(): string {
    return this.selectNoneSelected ? `You have selected No ${this.title}` : `You can select No ${this.title}`;
  }

  get dataSectionString(): string {
    const itemSection = this.itemSection?.id ? this.itemSection.id.toLowerCase() : this.title.toLowerCase();    

    if (itemSection.includes('protein')) {
      return 'protein-or-veggie';
    } else if (itemSection.includes('chips & dips') || itemSection.includes('chipssidescontentgroup')) {
      return 'chips-and-dips';
    } else if (itemSection.includes('single sides') || itemSection.includes('singlesidescontentgroup')) {
      return 'single-side';
    } else if (itemSection.includes('dip')) {
      return 'included-sides';
    } else if (itemSection.includes('addon') || itemSection.includes('premiumcontentgroup')) {
      return 'add-ons';
    } else if (itemSection.includes('tortilla')) {
      return 'tortilla';
    } else if (itemSection.includes('rice')) {
      return 'rice';
    } else if (itemSection.includes('beans')) {
      return 'beans';
    } else if (itemSection.includes('toppingscontentgroup') || itemSection.includes('included toppings')) {
      return 'included-toppings';
    } else if (itemSection.includes('toppings')) {
      return 'toppings';
    } else if (itemSection.includes('option')) {
      return 'options';
    } else if (itemSection.includes('side')) {
      return 'side';
    } else if (itemSection.includes('drink')) {
      return 'drinks';
    } else if (itemSection.includes('dessert')) {
      return 'desserts';
    } else if (itemSection.includes('fillingscontentgroup')) {
      return 'optional-add-ins';
    } else {
      return '';
    }
  }

  // Used only for automated testing purposes
  get dataQATitle(): string {
    return capitalizeEveryWord(this.title);
  }

  get isMissingSection(): boolean {
    return this.$appStore.state.mealBuilder.missingItemSections.indexOf(this.title) > -1;
  }

  get selectedRestaurant(): Restaurant | null {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get universalItemDetails(): UniversalItemDetails[] {
    if(!this.universalItems) {
      return [];
    }
    return this.universalItems.map(universalItem => this.processUniversalItemDetails(universalItem));
  }
  
  get isDesktop(): boolean {
    return this.$appStore.state.ui.clientWidth >= ui.breakpoints.desktop;
  }

  get isMealBuilderMobileCustomizationsExperienceEnabled(): boolean {
    return this.$services.adobeTarget.isMealBuilderMobileCustomizationsExperienceEnabled();
  }

  get displayVariantExperience(): boolean {
    return this.isMealBuilderMobileCustomizationsExperienceEnabled && !this.isDesktop;
  }

  processUniversalItemDetails(item: UniversalMenuItem): UniversalItemDetails {
    const calories = `${item.nutrition?.value}`;
    let tagline, promo, rewardsText;
    let itemExclusive = false;

    item.tags?.forEach((tag: Tag) => {
      if (tag.type === TagType.Tagline) {
        tagline = tag.value;
      }
      if (tag.type === TagType.Promo) {
        promo = tag.value;
      }
    })

    const metadata = this.$appStore.state.menu.metadata?.items[item.itemId];

    if (metadata) {
      itemExclusive = isMenuItemRewardsExclusive(metadata.tags);
      rewardsText = getRewardsExclusiveTagText(metadata.tags)
    }

    return {
      itemId: item.itemId,
      name: item.primaryFillingName,
      metadata: metadata,
      calories: calories,
      thumbnailImageUrl: item.thumbnailUrl,
      tagline: tagline,
      promo: promo,
      isItemExclusive: itemExclusive,
      rewardsText: rewardsText
    }
  }

  processItemDetails(item: SelectableMenuItem): ItemSelectorDetails | undefined {
    const metadata = findMetadataForMenuItem(item.item.itemId, this.metadata);
    if (!metadata || !this.builderMode) {
      return undefined;
    }
    const defaultItemName = item.item.itemName;
    const defaultItemSelected = this.$services.mealBuilder.isItemSelected(item);
    const selected = this.isItemSelected ? this.isItemSelected(item) || defaultItemSelected : defaultItemSelected;
    const customizingItemId = this.customizingItem?.item.itemId;
    const isCustomizing = customizingItemId === item.item.itemId;
    const isAvailable = item.item.isItemAvailable;

    const variant = this.$appStore.state.mealBuilder.selectedVariant;
    const portion = this.$services.mealBuilder.getDisplayedItemPortion(this.builderMode, item, variant);
    let formattedPrice;

    // Grouped Items
    let formattedCalorieRange = '';
    let customizations = item.customizations;
    let isGroupedItem = false;
    let selectedText;
    const customizeText = item.customizeTextForGroupedItems?.toUpperCase() ?? 'CUSTOMIZE';
    if (item.groupedItemSelectionOptions) {
      if (item.displayRangeFormatForGroupedItems) {
        formattedPrice = selected
          ? (this.displayVariantExperience ? this.$services.mealBuilder.getEffectiveItemPrice(this.builderMode, item, variant).formattedPrice : this.formatPriceWithGroupedItemInformation(item))
          : formatPriceRange(this.$services.utils, map(item.groupedItemSelectionOptions, 'price'));
        formattedCalorieRange = formatCalorieRange(item.groupedItemsCalorieRange);
      } else if (!selected && item.defaultItemId && item.defaultItemId !== item.item.itemId) {
        // Grouped item unselected without range must return to default item
        item.item = { ...item.item, ...getDefaultGroupedMenuItem(item) };
      }

      customizations = this.buildCustomizationsForGroupedItem(item);

      if (!item.customizations.some(c => c.selected)) {
        // Only find selectedText if there's no selected normal customization
        selectedText = (customizations as SelectableItemCardCustomization[]).find(c => c.customization?.id === item.item.itemId)?.customization?.selectedText;
      } else if (item.defaultItemId !== item.item.itemId) {
        // If an item has mixed customizations and a normal customization is selected, item needs to revert to default CMG-ID
        item.item = { ...item.item, ...getDefaultGroupedMenuItem(item) };
      }
      isGroupedItem = true;
    }

    if (!formattedPrice) {
      // Selected grouped items and regular menu items at this point
      formattedPrice = this.$services.mealBuilder.getEffectiveItemPrice(this.builderMode, item, variant).formattedPrice;
    }

    const nutrition = metadata.nutrition;
    const itemCalories = nutrition?.find(n => n.name == NutritionFactName.Calories) ?? '';
    const nonGroupedItemCalories = !!itemCalories ? itemCalories.value : '';

    const tags = metadata.tags ?? [];
    const promoTag = tags.find(t => t.type == TagType.Promo && t.value != 'nchide');
    const taglineTag = tags.find(t => t.type == TagType.Tagline);
    const personalizedItemDetails = this.$appStore.state.adobeTarget.mealBuilderItemDetails?.find(itemDetail => itemDetail.cmgIds.find(id=> id == item.item.itemId));
    const details: ItemSelectorDetails = {
      item,
      metadata,
      name: personalizedItemDetails?.itemName ?? item.displayNameForGroupedItems ?? (this.itemNameSelector ? this.itemNameSelector(item) ?? defaultItemName : defaultItemName),
      formattedPrice: this.variantPriceType == PriceType.Single ? null: formattedPrice,
      selected,
      customizing: isCustomizing,
      disabled: !!this.customizingItem && !isCustomizing,
      thumbnailImageUrl: metadata.thumbnailUrl,
      portion: portion ?? null,
      customizations,
      quantity: item.selectionInfo.kind == ItemCardSelectionInfoKind.Quantity ? item.selectionInfo.quantity : null,
      type: item.selectionInfo.kind,
      message: item.message || null,
      calories: selected ? nonGroupedItemCalories : formattedCalorieRange || nonGroupedItemCalories,
      promo: personalizedItemDetails?.promoTag ?? promoTag?.value ?? null,
      tagline: personalizedItemDetails?.tagLine ?? taglineTag?.value ?? null,
      customizeText,
      isGroupedItem,
      isItemAvailable: isAvailable ?? null,
      eligibleForDelivery: item.item.eligibleForDelivery ?? null,
      isItemExclusive: personalizedItemDetails?.isRewardsExclusive ?? isMenuItemRewardsExclusive(metadata.tags),
      rewardsText: personalizedItemDetails?.rewardsText ?? getRewardsExclusiveTagText(metadata.tags),
      selectedText,
      shouldShowHalfOption: selected || !isAvailable ? false : this.shouldShowHalfOption(item)
    };

    if (details.isItemExclusive) {
      this.$emit('rewards-exclusive-item-found', details.rewardsText);
    }

    return details;
  }

  onItemSelected<TArgs>(item: SelectableMenuItem, args: TArgs) {
    if (!!this.customizingItem) {
      this.$services.mealBuilder.setCustomizingItem(null);
    }

    const noneArray: string[] = ['CMG-5254', 'CMG-5556', 'CMG-5003', 'CMG-5053'];

    if(noneArray.includes(item.item.itemId)) {
      this.$emit('select-none-selected');
    }

    this.$emit('item-selected', <ItemSelectedEventArgs>{ item, args });

    if (!this.isDesktop && !this.isMealBuilderMobileCustomizationsExperienceEnabled) {
      this.$services.mealBuilder.setActiveItemId(item.item.itemId);
    }
  }

  onItemCustomizationSelected(item: SelectableMenuItem, customization: ItemCardCustomization) {
    const variant = this.$appStore.state.mealBuilder.selectedVariant;

    // See if we're trying to customize an unselected item; if so, go ahead and select it
    if (this.builderMode) {
      if (item.itemSource === SelectableMenuItemSource.Variant) {
        if (!this.$services.mealBuilder.isItemDisplayedAsSelected(this.builderMode, item)) {
          this.$services.mealBuilder.selectVariantSafe(
            this.builderMode,
            item as SelectableVariantMenuItem,
            this.$appStore.state.mealBuilder.selectedVariant,
            this.$appStore.state.mealBuilder.displayedVariants,
            true,
          );
        }
      } else if (item.selectionInfo.kind == ItemCardSelectionInfoKind.Quantity) {
        if (!this.$services.mealBuilder.isItemSelected(item) && this.itemSection) {
          const effectiveVariant = variant || this.$services.mealBuilder.getDefaultVariant(this.builderMode) || null;
          this.$services.mealBuilder.selectItemSafe(item, this.itemSection, effectiveVariant, 1, true);
        }
      } else {
        if (!this.$services.mealBuilder.isItemSelected(item) && this.itemSection) {
          const effectiveVariant = variant || this.$services.mealBuilder.getDefaultVariant(this.builderMode) || null;
          this.$services.mealBuilder.selectItemSafe(item, this.itemSection, effectiveVariant, true, true);
        }
      }
    }

    // If the customization is already selected, bail
    if (customization.selected) {
      return;
    }

    this.$emit('item-customization-selected', { item, customization });
  }

  onItemRequestCustomizationState(item: SelectableMenuItem, customizing: boolean) {
    if (customizing) {
      this.$services.mealBuilder.setCustomizingItem(item);
    } else if (this.customizingItem && this.customizingItem == item) {
      this.$services.mealBuilder.setCustomizingItem(null);
    }
  }

  onSelectNoneSelected() {
    this.$emit('select-none-selected');
  }

  buildCustomizationsForGroupedItem(item: SelectableMenuItem): ItemCardCustomization[] {
    if (!item.groupedItemSelectionOptions) {
      return [];
    }

    const customizations = item.groupedItemSelectionOptions.map(groupedItem => {
      // Show name on pill for item as provided in the metadata, or use a range e.g. `Normal`, `Extra + $2.00`, `22 fl oz - $3.50`
      let customizationName = groupedItem.asCustomizationName ?? groupedItem.name;
      const isDelivery = this.$services.order.isDelivery();
      if (groupedItem.showCustomizationPrice && groupedItem.asCustomizationName) {
        const defaultItem = item.groupedItemSelectionOptions?.find(i => i.id === item.defaultItemId);
        const defaultItemPrice = (isDelivery ? defaultItem?.deliveryPrice : defaultItem?.price) ?? 0;
        const price = (isDelivery ? groupedItem.deliveryPrice : groupedItem.price) - defaultItemPrice; // Get difference so we can do +$2.00 or whatever
        customizationName = `${customizationName} +${this.$services.utils.formatPrice(price)}`;
      }
      if (!groupedItem.asCustomizationName) {
        // portionNutritionInfo contains the sizing details required for the customization name e.g. 22 fl oz
        const portionNutritionInfo = groupedItem.nutrition.find(info => info.name === 'Portion');
        if (portionNutritionInfo) {
          customizationName = this.displayVariantExperience ? `${portionNutritionInfo.value} ${portionNutritionInfo.unit}` : `${portionNutritionInfo.value} ${portionNutritionInfo.unit} - ${this.$services.utils.formatPrice(
            (isDelivery ? groupedItem.deliveryPrice : groupedItem.price) ?? groupedItem.price,
          )}`.toUpperCase();
        }
      }

      // Empty string denotes showing the standard check mark
      const selectedText = groupedItem.showCustomizationNameWhenSelected ? groupedItem.asCustomizationName : '';

      return {
        customization: {
          countTowardsContentMax: groupedItem.countTowardsContentMax,
          countTowardsCustomizationMax: groupedItem.countTowardsCustomizationMax,
          id: groupedItem.id,
          name: groupedItem.name,
          unitPrice: groupedItem.price,
          unitDeliveryPrice: groupedItem.deliveryPrice,
          posId: groupedItem.posId,
          selectedText,
        } as GroupedItemCustomization,
        hidden: false,
        iconText: groupedItem.name,
        text: customizationName,
        type: ItemCardCustomizationType.SelectableItem,
        item,
        // Show selected only if it is the proper groupedItem that is the main selected item, and if there are no other _actual_ customizations selected (half/half, side)
        selected:
          groupedItem.id === item.item.itemId &&
          ((item.selectionInfo.kind === ItemCardSelectionInfoKind.Single && item.selectionInfo.selected) || 
          (item.selectionInfo.kind === ItemCardSelectionInfoKind.Quantity && this.displayVariantExperience)) &&
          !item.customizations.some(c => c.selected),
      };
    });

    // Sort '32 oz' before '22 oz' and 'Normal' before 'Extra' for UI presentation
    // TODO tech debt - do this via a field on MMD for grouped items
    const sortedCustomizations = item.displayRangeFormatForGroupedItems && !this.displayVariantExperience
      ? customizations.sort((a, b) => (a.customization.name < b.customization.name ? 1 : -1))
      : customizations.sort((a, b) => (a.customization.name > b.customization.name ? 1 : -1));

    // Include any normal customizations with grouped item customizations e.g. extra cauli rice is CMG-1039, but side cauli rice is CMG-1036 with a customization number
    return [...sortedCustomizations, ...(item.customizations ?? [])] as ItemCardCustomization[];
  }

  // Returns e.g. '22 FL OZ'
  formatPriceWithGroupedItemInformation(item: SelectableMenuItem): string {
    if (!item.groupedItemSelectionOptions) {
      return '';
    }

    // Find the currently selected item in the groupedItemSelectionOptions
    const groupedItemInfo = item.groupedItemSelectionOptions.find(groupedItem => groupedItem.id === item.item.itemId);
    if (groupedItemInfo) {
      const portionNutritionInfo = groupedItemInfo.nutrition.find(info => info.name === 'Portion');
      if (portionNutritionInfo) {
        return `${this.$services.utils.formatPrice(groupedItemInfo.price)} - ${portionNutritionInfo.value} ${portionNutritionInfo.unit}`.toUpperCase();
      }
    }
    return '';
  }

  mounted() {
    this.$services.mealBuilder.setErrorEventSent('UniversalMealBuilderErrorEvent', false);
  }

  shouldShowHalfOption(item: SelectableMenuItem) {
    if (this.builderMode) {
      const selectedVariant = this.$appStore.state.mealBuilder.selectedVariant;
      const selectedItemsInSection = this.items.filter(i => this.$services.mealBuilder.isItemDisplayedAsSelected(this.builderMode!, i));
      
      if (item.itemSource == SelectableMenuItemSource.Variant && selectedVariant) {
        return selectedVariant.item.maxHalfs == 1 && (item.item as VariantMenuItem).maxHalfs == 1 && selectedItemsInSection?.length == 1;
      } else {
        const effectiveVariant = selectedVariant || this.$services.mealBuilder.getDefaultVariant(this.builderMode) || null;
        const matchingItem = effectiveVariant?.item.contents?.find(c => c.itemId == item.item.itemId);
        const selectedItemsWithHalfOption = selectedItemsInSection.filter(i => i.customizations.some(c => c.text == 'Half'));

        if (matchingItem) {
          const halfCustomization = matchingItem.customizations?.find(c => c.name == 'Half');
          return !!halfCustomization && selectedItemsWithHalfOption?.length == 1;
        }
      }
    }

    return false;
  }
}
